import React, { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { IoIosArrowUp, IoIosArrowDown } from 'react-icons/io';

export interface DropdownOption {
    value: string;
    label: string;
}

interface DropdownProps {
    options: DropdownOption[];
    isSearchable?: boolean;
    placeholder?: string;
    onSelect: (option: DropdownOption) => void;
    value?: string; // Expect the ID here
}

const Dropdown: React.FC<DropdownProps> = ({
    options,
    isSearchable = false,
    placeholder = 'Select an option',
    onSelect,
    value,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
    );

    const handleSelect = (option: DropdownOption) => {
        setSelectedValue(option.label);
        setIsOpen(false);
        setSearchValue('');
        onSelect(option); // Pass the selected ID
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (value) {
            const selectedOption = options.find(option => option.value === value);
            setSelectedValue(selectedOption ? selectedOption.label : null);
        } else {
            setSelectedValue(null);
        }
    }, [value, options]); // Update when value or options change

    return (
        <div ref={dropdownRef} className="relative w-full">
            <div
                className={`text-sm cursor-pointer py-3 px-4 flex justify-between items-center bg-white border border-gray-300 ${isOpen ? 'rounded-t-lg' : 'rounded-lg'
                    }`}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    color: selectedValue ? '#333333' : '#A2A2A2',
                    borderBottomLeftRadius: isOpen ? '0px' : '8px',
                    borderBottomRightRadius: isOpen ? '0px' : '8px',
                }}
            >
                <span>{selectedValue ? selectedValue : placeholder}</span>
                <span>{isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}</span>
            </div>
            {isOpen && (
                <div className="absolute z-10 left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg">
                    {isSearchable && (
                        <div className="relative mb-2 p-2">
                            <input
                                type="text"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                placeholder="Search here..."
                                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none"
                                style={{ color: '#333333' }}
                            />
                            <FaSearch
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                        </div>
                    )}
                    <ul className="max-h-48 overflow-y-auto border-r border-b border-l border-gray-200">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <li
                                    key={option.value}
                                    className="cursor-pointer py-3 px-4 hover:bg-gray-100 text-sm border-t border-[#f2f2f2]"
                                    onClick={() => handleSelect(option)}
                                >
                                    {option.label}
                                </li>
                            ))
                        ) : (
                            <li className="py-2 px-4 text-gray-500 text-sm">No results found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Dropdown;
